/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React from "react";
import { AddLangToPath } from "@/helper/AddLanguagePath";

interface PolicyPopupLinkProps {
  /** Site-relative path, without a language prefix. Example: `/refund-policy/`. */
  path: string;
  /** Visible link text, already localised by the caller. */
  label: string;
  /**
   * Desktop opens a 600x600 popup; everything else opens a normal tab. The
   * caller decides, because the footer already resolves device type once and
   * passing it down avoids a second detection with a different answer.
   */
  isDesktopDevice: boolean;
  className?: string;
}

/**
 * A legal-policy link that opens in a small popup window on desktop.
 *
 * Both footer policy links render through this component so they cannot drift
 * apart. Two links sitting side by side that look identical but behave
 * differently is a defect users notice and reviewers report, and it is exactly
 * what happens when the popup logic is copied rather than shared.
 */
export const PolicyPopupLink: React.FC<PolicyPopupLinkProps> = ({
  path,
  label,
  isDesktopDevice,
  className = "footer-text",
}): React.JSX.Element => {
  const href = AddLangToPath(`${path}?standalone=true`);

  if (!isDesktopDevice) {
    return (
      <a
        className={className}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
      >
        {label}
      </a>
    );
  }

  return (
    <a
      className={className}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => {
        // Let the browser handle modified clicks — a user asking for a new tab
        // or window should get one, not a popup.
        if (
          e.button === 0 &&
          !e.ctrlKey &&
          !e.shiftKey &&
          !e.metaKey &&
          !e.altKey
        ) {
          e.preventDefault();
          const newWindow = window.open(
            href,
            "_blank",
            "width=600,height=600,left=0,top=0,noopener,noreferrer",
          );

          // Popup blockers return null or an immediately closed window. Fall
          // back to a normal tab so the link never silently does nothing.
          if (
            !newWindow ||
            newWindow.closed ||
            typeof newWindow.closed === "undefined"
          ) {
            window.open(href, "_blank", "noopener,noreferrer");
          }
        }
      }}
    >
      {label}
    </a>
  );
};

export default PolicyPopupLink;
